import { clearAppState, refreshToken as refreshService } from '@shopmuchmore/api'
import axios from 'axios'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { queryClient } from '../../lib/queryClient'
import { resetCartStore, useCartStore } from '../cart/useCartStore'
import { useLocationStore } from '../local/store/location.store'
import { DEFAULT_COORDINATES } from '../local/utils/geocoding.utils'
import { resetNotificationStore } from '../notifications/notifications.store'
import type { CustomerProfile, LoginResponse } from './auth.types'

// Dedicated client for refresh to avoid circular dependency
const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
})

interface AppSettings {
  theme: 'light' | 'dark'
  sidebarOpen: boolean
}

// Ensure this matches the AuthStoreState interface from @shopmuchmore/api
interface AuthState {
  customer: CustomerProfile | null
  login: (loginResponse: LoginResponse) => void
  appSettings: AppSettings
  setCustomer: (customer: CustomerProfile | null) => void
  setTheme: (theme: 'light' | 'dark') => void
  toggleSidebar: () => void
  logout: () => void

  // Auth Store Interface
  accessToken: string | null
  refreshToken: string | null
  setAccessToken: (accessToken: string | null) => void
  refresh: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        customer: null,
        accessToken: null,
        refreshToken: null,

        setAccessToken: (accessToken: string | null) => set({ accessToken }),

        login: (loginResponse: LoginResponse) => {
          set({
            customer: loginResponse.user,
            accessToken: loginResponse.tokens.access,
            refreshToken: loginResponse.tokens.refresh,
          })
          // Clear guest cart ID on login so authenticated calls never send guest cart_id
          useCartStore.getState().setCartId(null)
          queryClient.invalidateQueries({ queryKey: ['cart'] })
        },

        appSettings: {
          theme: 'light',
          sidebarOpen: true,
        },
        setCustomer: (customer) => set({ customer }),
        setTheme: (theme) => set((state) => ({ appSettings: { ...state.appSettings, theme } })),
        toggleSidebar: () =>
          set((state) => ({ appSettings: { ...state.appSettings, sidebarOpen: !state.appSettings.sidebarOpen } })),

        logout: () => {
          set({
            customer: null,
            accessToken: null,
            refreshToken: null,
            appSettings: {
              theme: 'light',
              sidebarOpen: true,
            },
          })
          clearAppState(queryClient, [resetCartStore, resetNotificationStore])

          // Preserve active browsing location as a manual guest address so sign out doesn't lose the chosen location
          const currentLocation = useLocationStore.getState()
          if (currentLocation.address && currentLocation.method === 'saved') {
            currentLocation.setLocation(
              currentLocation.latitude ?? DEFAULT_COORDINATES.lat,
              currentLocation.longitude ?? DEFAULT_COORDINATES.lng,
              currentLocation.address,
              'manual',
            )
          }
        },

        refresh: async () => {
          const { refreshToken, logout } = get()
          if (!refreshToken) return false

          try {
            const response = await refreshService(refreshClient, refreshToken)
            if (response.tokens.access) {
              set({
                accessToken: response.tokens.access,
                refreshToken: response.tokens.refresh,
              })
              return true
            }
            logout()
            return false
          } catch (error) {
            console.error('Token refresh failed', error)
            logout()
            return false
          }
        },
      }),
      {
        name: 'app-storage',
        // IMPORTANT: persistent storage only for refresh token and user prefs
        // Access token is deliberately excluded to be in-memory only
        partialize: (state) => ({
          theme: state.appSettings.theme,
          sidebarOpen: state.appSettings.sidebarOpen,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          customer: state.customer, // persist user profile
        }),
      },
    ),
    { name: 'AppStore' },
  ),
)
